'use client';

/**
 * PremiumGate Component
 * Displays a paywall for premium content with upgrade CTA
 */

import { useAuth } from '@/lib/auth';
import Link from 'next/link';
import { useEffect, useState } from 'react';

interface PremiumGateProps {
  requiredTier: 'free' | 'pro' | 'team';
  contentType?: string;
  contentSlug?: string;
}

interface UserSubscription {
  plan: 'free' | 'pro' | 'team';
  status: string;
}

const TIER_HIERARCHY = {
  free: 0,
  pro: 1,
  team: 2,
};

export default function PremiumGate({
  requiredTier,
  contentType = 'content',
  contentSlug,
}: PremiumGateProps) {
  const { user, isAuthenticated, isLoading, login } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);

  // Fetch user subscription status
  useEffect(() => {
    async function fetchSubscription() {
      if (!isAuthenticated || !user) {
        setLoadingSubscription(false);
        return;
      }

      try {
        const response = await fetch('/api/secure/dashboard', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setSubscription(data.data.subscription);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      } finally {
        setLoadingSubscription(false);
      }
    }

    fetchSubscription();
  }, [isAuthenticated, user]);

  // Track gate view analytics
  useEffect(() => {
    if (!isLoading && !loadingSubscription) {
      const userTier = subscription?.plan || 'free';
      const hasAccess = TIER_HIERARCHY[userTier] >= TIER_HIERARCHY[requiredTier];

      // Track premium gate view
      fetch('/api/public/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'premium_gate_view',
          properties: {
            contentType,
            contentSlug,
            requiredTier,
            userTier,
            hasAccess,
            authenticated: isAuthenticated,
          },
        }),
      }).catch(console.error);
    }
  }, [isLoading, loadingSubscription, subscription, requiredTier, contentType, contentSlug, isAuthenticated]);

  // Show loading state
  if (isLoading || loadingSubscription) {
    return (
      <div className="border border-border rounded-lg p-12 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-accent-muted rounded w-2/3 mx-auto mb-4"></div>
          <div className="h-4 bg-accent-muted rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  // Check if user has access
  const userTier = subscription?.plan || 'free';
  const hasAccess = TIER_HIERARCHY[userTier] >= TIER_HIERARCHY[requiredTier];

  // If user has access, don't show gate
  if (hasAccess) {
    return null;
  }

  // Determine CTA based on authentication status
  const handleUpgradeClick = () => {
    // Track upgrade click
    fetch('/api/public/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'premium_gate_upgrade_click',
        properties: {
          contentType,
          contentSlug,
          requiredTier,
          userTier,
          authenticated: isAuthenticated,
        },
      }),
    }).catch(console.error);
  };

  return (
    <div className="relative my-12">
      {/* Gradient fade effect */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-background pointer-events-none -mt-24"></div>

      {/* Premium gate card */}
      <div className="border-2 border-border rounded-lg p-8 md:p-12 text-center bg-background/50 backdrop-blur-sm">
        {/* Lock icon */}
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent-muted border border-border">
            <svg
              className="w-8 h-8 text-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
          {isAuthenticated ? 'Upgrade to Continue Reading' : 'Premium Content'}
        </h3>

        {/* Description */}
        <p className="text-foreground-secondary text-lg mb-8 max-w-2xl mx-auto">
          {isAuthenticated ? (
            <>
              This {contentType} is part of our <span className="text-accent font-semibold">{requiredTier === 'pro' ? 'Pro' : 'Team'}</span> plan.
              Upgrade now to unlock this content and access our complete library of frameworks, tools, and resources.
            </>
          ) : (
            <>
              Sign in or create a free account to preview this content.
              Upgrade to <span className="text-accent font-semibold">{requiredTier === 'pro' ? 'Pro' : 'Team'}</span> for full access.
            </>
          )}
        </p>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-8 text-left max-w-3xl mx-auto">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-accent flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="font-semibold text-foreground mb-1">Premium Content</h4>
              <p className="text-sm text-foreground-tertiary">Access all frameworks & playbooks</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-accent flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="font-semibold text-foreground mb-1">Interactive Tools</h4>
              <p className="text-sm text-foreground-tertiary">Decision matrices & calculators</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-accent flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="font-semibold text-foreground mb-1">Templates & Docs</h4>
              <p className="text-sm text-foreground-tertiary">Ready-to-use resources</p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {!isAuthenticated ? (
            <>
              <button
                onClick={() => {
                  setLoginLoading(true);
                  login({ returnTo: window.location.pathname });
                }}
                disabled={loginLoading}
                className="px-8 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-70 disabled:cursor-wait flex items-center gap-2"
              >
                {loginLoading && (
                  <div className="w-4 h-4 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
                )}
                Sign In to Preview
              </button>
              <Link
                href="/pricing"
                onClick={handleUpgradeClick}
                className="px-8 py-3 border-2 border-accent text-accent font-semibold rounded-lg hover:bg-accent-muted transition-colors"
              >
                View Pricing
              </Link>
            </>
          ) : (
            <>
              <Link
                href="/pricing"
                onClick={handleUpgradeClick}
                className="px-8 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Upgrade to {requiredTier === 'pro' ? 'Pro' : 'Team'}
              </Link>
              <Link
                href="/pricing"
                className="px-8 py-3 border-2 border-accent text-accent font-semibold rounded-lg hover:bg-accent-muted transition-colors"
              >
                Compare Plans
              </Link>
            </>
          )}
        </div>

        {/* Money-back guarantee */}
        <p className="text-sm text-foreground-tertiary mt-6">
          30-day money-back guarantee • Cancel anytime
        </p>
      </div>
    </div>
  );
}
